import React, {
  memo,
  useMemo,
  useState,
} from 'react';

import { Button } from 'antd';
import { PiPencilSimple } from 'react-icons/pi';
import { BottomSheet } from 'react-spring-bottom-sheet';
import TMIcon from 'tm-icons-library';

import { CheckCircleOutlined } from '@ant-design/icons';

import BottomSheetTile
  from '../../../../components/BottomSheetTile/BottomSheetTile';
import { useTheme } from '../../../../context/ThemeProvider';
import {
  setCreateOrViewOfferModalState,
  setServiceRequestModalState,
  setShareFeedbackModalState,
} from '../../../../context/tqConnectServiceRequestReducer';
import {
  useAppDispatch,
  useAppNavigate,
  useAppSelector,
} from '../../../../shared/hooks';
import { ROUTES } from '../../../../types/routes';
import {
  ITQServiceRequest,
  ITQServiceRequestDetails,
  ITQServiceRequestResponsesResult,
  TQServiceRequestTypeValue,
} from '../../../../types/tqConnectTypes/service-requests';
import AcceptOrRejectPrivateServiceRequest
  from '../../screens/ServiceRequestDetails/AcceptOrRejectPrivateServiceRequest';
import DeleteServiceRequestModal from './DeleteServiceRequestModal';

const ICON_SIZE = 20;

enum ACTION_KEYS {
  EDIT_SERVICE_REQUEST = 'edit-service-request',
  DUPLICATE_SERVICE_REQUEST = 'duplicate-service-request',
  DELETE_SERVICE_REQUEST = 'delete-service-request',
  VIEW_REQUEST_DETAILS = 'view-request-details',
}

interface Props {
  serviceRequestId: string;
  serviceRequestDetails: ITQServiceRequestDetails;
  isMyRequest: boolean;
  canOfferHelp: boolean;
  canMarkAsCompleted: boolean;
  cameFrom: TQServiceRequestTypeValue;
  onGoBack: () => void;
  isFooter: boolean;
  isRequestedToMePrivately: boolean;
  serviceRequestResponsesResult: ITQServiceRequestResponsesResult;
  onViewRequestDetails?: () => void;
}

const ServiceRequestActions: React.FC<Props> = ({
  serviceRequestId,
  serviceRequestDetails,
  isMyRequest,
  canOfferHelp,
  canMarkAsCompleted,
  cameFrom,
  onGoBack,
  isFooter,
  isRequestedToMePrivately,
  serviceRequestResponsesResult,
  onViewRequestDetails,
}) => {
  const navigate = useAppNavigate();

  const { colors: COLORS } = useTheme();

  const dispatch = useAppDispatch();

  const {
    id: userId,
    name,
    profilePic: profilePicUrl,
    tqConnectScore,
    tqConnectBadge,
  } = useAppSelector((state) => state.user);

  const {
    title,
    description,
    tags,
    visibility,
    status,
    requestedTo,
    attachments,
    acceptedResponse,
  } = serviceRequestDetails;

  const [isActionsBottomSheetOpen, setIsActionsBottomSheetOpen] =
    useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const ACTIONS = useMemo(() => {
    return [
      {
        key: ACTION_KEYS.EDIT_SERVICE_REQUEST,
        label: 'Edit',
        icon: (size = ICON_SIZE, color = 'currentColor') => (
          <PiPencilSimple size={size} color={color} />
        ),
        onClick: () => {
          dispatch(
            setServiceRequestModalState({
              isOpen: true,
              serviceRequestId,
              autoCloseOnSuccess: true,
              requestedTo,
              serviceRequest: {
                _id: serviceRequestId,
                title,
                description,
                tags,
                visibility,
                status,
                requestedTo,
                attachments,
              },
            }),
          );
          navigate(ROUTES.TQ_CONNECT_SERVICE_REQUESTS_CREATE);
        },
        allowed: isMyRequest && status === 'open',
      },
      {
        key: ACTION_KEYS.DUPLICATE_SERVICE_REQUEST,
        label: 'Duplicate',
        icon: (size = ICON_SIZE, color = 'currentColor') => (
          <TMIcon name="copy-outline" size={size} color={color} />
        ),
        onClick: () => {
          dispatch(
            setServiceRequestModalState({
              isOpen: true,
              serviceRequestId: undefined,
              autoCloseOnSuccess: true,
              ...(visibility === 'private' && { requestedTo }),
              serviceRequest: {
                title,
                description,
                tags,
                visibility,
                status: 'open',
                ...(visibility === 'private' && { requestedTo }),
                attachments,
              },
              callback: (serviceRequest?: Partial<ITQServiceRequest>) => {
                navigate(
                  ROUTES.TQ_CONNECT_SERVICE_REQUESTS_DETAILS.replace(
                    ':serviceRequestId',
                    serviceRequest?._id || '',
                  ) as ROUTES,
                  {
                    search:
                      !cameFrom || cameFrom === 'discover'
                        ? ''
                        : `?from=${cameFrom}`,
                  },
                );
              },
            }),
          );
          navigate(ROUTES.TQ_CONNECT_SERVICE_REQUESTS_CREATE);
        },
        allowed: isMyRequest,
      },
      {
        key: ACTION_KEYS.DELETE_SERVICE_REQUEST,
        label: 'Delete',
        type: 'error',
        icon: (size = ICON_SIZE, color = 'currentColor') => (
          <TMIcon name="trash-outline" size={size} color={color} />
        ),
        onClick: () => {
          setIsDeleteModalOpen(true);
        },
        allowed: isMyRequest && status === 'open',
      },
      {
        key: ACTION_KEYS.VIEW_REQUEST_DETAILS,
        label: 'View request details',
        icon: (size = ICON_SIZE, color = 'currentColor') => (
          <TMIcon name="eye-outline" size={size} color={color} />
        ),
        onClick: () => {
          onViewRequestDetails?.();
        },
        allowed: isMyRequest && Boolean(onViewRequestDetails),
      },
    ].filter((action) => action.allowed);
  }, [
    isMyRequest,
    status,
    dispatch,
    serviceRequestId,
    requestedTo,
    title,
    description,
    tags,
    visibility,
    attachments,
    navigate,
    cameFrom,
    onViewRequestDetails,
  ]);

  return (
    <>
      <div className="service-request__actions">
        {canOfferHelp && isFooter && (
          <Button
            className="siteBtn darkBtn"
            onClick={() => {
              dispatch(
                setCreateOrViewOfferModalState({
                  isOpen: true,
                  mode: 'create',
                  serviceRequestId,
                  respondedBy: {
                    _id: userId || '',
                    name: name || '',
                    profilePicUrl: profilePicUrl || '',
                    tqConnectScore: tqConnectScore || 0,
                    tqConnectBadge: tqConnectBadge || '',
                    tqReviewReceived: 0,
                  },
                }),
              );
              navigate(ROUTES.TQ_CONNECT_HELP_OFFER_FORM, {
                serviceRequestId,
              });
            }}>
            Offer help
          </Button>
        )}

        {canMarkAsCompleted && isFooter && (
          <Button
            className="siteBtn darkBtn"
            onClick={() => {
              dispatch(
                setShareFeedbackModalState({
                  isOpen: true,
                  serviceRequestId,
                  serviceRequestResponseId: acceptedResponse?._id,
                  respondedBy: acceptedResponse?.respondedBy,
                }),
              );
              navigate(ROUTES.TQ_CONNECT_SERVICE_MARK_AS_COMPLETE, {
                serviceRequestId,
              });
            }}>
            <CheckCircleOutlined /> Mark as completed
          </Button>
        )}

        {isRequestedToMePrivately &&
        isFooter &&
        serviceRequestDetails?.status === 'open' ? (
          <AcceptOrRejectPrivateServiceRequest
            serviceRequestId={serviceRequestId || ''}
            serviceRequestDetails={serviceRequestDetails}
            privateResponse={serviceRequestResponsesResult?.myResponse}
          />
        ) : null}

        {isMyRequest && !isFooter && (
          <>
            <button
              type="button"
              className="header--activity__action"
              style={{ background: 'transparent', border: 'none' }}
              onClick={() => setIsActionsBottomSheetOpen(true)}
              aria-label="More actions">
              <TMIcon
                name="ellipsis-horizontal"
                color={COLORS.ICON}
                size={ICON_SIZE}
              />
            </button>
            <BottomSheet
              open={isActionsBottomSheetOpen}
              onDismiss={() => setIsActionsBottomSheetOpen(false)}>
              {ACTIONS.map((action) => (
                <BottomSheetTile
                  key={action.key}
                  icon={action.icon(
                    ICON_SIZE,
                    action?.type === 'error' ? COLORS.ERROR : COLORS.ICON,
                  )}
                  title={action.label}
                  warning={action?.type === 'error'}
                  onClick={() => {
                    setIsActionsBottomSheetOpen(false);
                    action.onClick();
                  }}
                />
              ))}
            </BottomSheet>
          </>
        )}
      </div>

      <DeleteServiceRequestModal
        serviceRequestId={serviceRequestId}
        serviceRequestDetails={serviceRequestDetails}
        isOpen={isDeleteModalOpen}
        closeModal={() => setIsDeleteModalOpen(false)}
        onDeleteSuccess={onGoBack}
      />
    </>
  );
};

export default memo(ServiceRequestActions);
